import { useTranslation } from 'react-i18next';
import { TimeSeriesChart } from './charts/TimeSeriesChart.js';
import { SalesPointPerformanceChart } from './charts/SalesPointPerformanceChart.js';
import { FunnelChart } from './charts/FunnelChart.js';
import { PaymentMixChart } from './charts/PaymentMixChart.js';
import { RFMChart } from './charts/RFMChart.js';
import type {
  TimeSeriesPoint,
  SalesPointAggregate,
  FunnelStep,
  PaymentMix,
  RfmBucket,
} from '../types/analytics.types.js';
import { AnChartSection } from './AnChartSection/index.js';

interface Props {
  timeseries?: TimeSeriesPoint[];
  salesPoints?: SalesPointAggregate[];
  funnel?: FunnelStep[];
  paymentMix?: PaymentMix[];
  rfm?: RfmBucket[];
  isLoading?: boolean;
}

export function AnalyticsCharts({
  timeseries,
  salesPoints,
  funnel,
  paymentMix,
  rfm,
  isLoading = false,
}: Props) {
  const { t } = useTranslation('admin');
  const showTimeseries = Array.isArray(timeseries);
  const showSalesPoints = Array.isArray(salesPoints);
  const showPaymentMix = Array.isArray(paymentMix);
  const showFunnel = Array.isArray(funnel);
  const showRfm = Array.isArray(rfm);
  const filterHint = t('analytics.revenue.emptyFiltersHint');

  return (
    <div className="grid grid-cols-1 gap-5 w-full">
      {showTimeseries ? (
        <AnChartSection sectionName={t('analytics.charts.timeSeriesTitle')}>
          <TimeSeriesChart
            data={timeseries ?? []}
            isLoading={isLoading}
            error={undefined}
            title={t('analytics.charts.timeSeriesTitle')}
            subtitle={t('analytics.charts.timeSeriesSubtitle')}
            emptyMessage={t('analytics.charts.emptyDefault')}
            emptyHint={t('analytics.charts.timeSeriesEmptyHint')}
          />
        </AnChartSection>
      ) : null}

      {showSalesPoints || showPaymentMix ? (
        <div
          className={
            showSalesPoints && showPaymentMix
              ? 'grid grid-cols-1 xl:grid-cols-2 gap-5 w-full'
              : 'w-full'
          }
        >
          {showSalesPoints ? (
            <AnChartSection sectionName={t('analytics.charts.kioskPerformanceTitle')}>
              <SalesPointPerformanceChart
                data={salesPoints ?? []}
                isLoading={isLoading}
                error={undefined}
                title={t('analytics.charts.kioskPerformanceTitle')}
                subtitle={t('analytics.charts.kioskPerformanceSubtitle')}
                emptyMessage={t('analytics.revenue.noKioskData')}
                emptyHint={filterHint}
              />
            </AnChartSection>
          ) : null}
          {showPaymentMix ? (
            <AnChartSection sectionName={t('analytics.tabs.payments')}>
              <PaymentMixChart
                data={paymentMix ?? []}
                isLoading={isLoading}
                error={undefined}
                title={t('analytics.chartsGrid.paymentMixTitle')}
                subtitle={t('analytics.chartsGrid.paymentMixSubtitle')}
                emptyMessage={t('analytics.chartsGrid.paymentMixEmpty')}
                emptyHint={filterHint}
              />
            </AnChartSection>
          ) : null}
        </div>
      ) : null}

      {showFunnel ? (
        <AnChartSection sectionName={t('analytics.tabs.funnel')}>
          <FunnelChart
            data={funnel ?? []}
            isLoading={isLoading}
            error={undefined}
            title={t('analytics.chartsGrid.funnelTitle')}
            subtitle={t('analytics.chartsGrid.funnelSubtitle')}
            emptyMessage={t('analytics.chartsGrid.funnelEmpty')}
            emptyHint={filterHint}
          />
        </AnChartSection>
      ) : null}

      {showRfm ? (
        <AnChartSection sectionName={t('analytics.charts.rfmTitle')}>
          <RFMChart
            data={rfm ?? []}
            isLoading={isLoading}
            error={undefined}
            title={t('analytics.charts.rfmTitle')}
            subtitle={t('analytics.charts.rfmSubtitle')}
            emptyMessage={t('analytics.chartsGrid.rfmEmpty')}
            emptyHint={filterHint}
          />
        </AnChartSection>
      ) : null}
    </div>
  );
}
